import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Zap, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/api";

export default function ReviewPage() {
  const { bookingId } = useParams();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/reviews/booking/${bookingId}`)
      .then((r) => setCtx(r.data))
      .catch((e) => setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "This review link isn't valid"));
  }, [bookingId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", { booking_id: bookingId, rating, message });
      setDone(true);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not send your review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center px-4" data-testid="review-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                  className="w-full max-w-md bg-[#0F172A] border border-amber-500/25 p-8">
        <div className="hazard-stripes h-2 mb-8" />
        <div className="flex items-center gap-2 mb-6">
          <span className="w-8 h-8 bg-amber-500 flex items-center justify-center">
            <Zap className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
          </span>
          <span className="font-condensed font-extrabold uppercase tracking-wide text-lg">
            Fix <span className="text-amber-500">&amp;</span> Finish
          </span>
        </div>

        {error && (
          <div data-testid="review-error">
            <h1 className="text-3xl font-condensed font-extrabold uppercase mb-3">Hmm, that link's off</h1>
            <p className="text-sm text-slate-400 mb-6">{error}</p>
            <Link to="/" data-testid="review-home-link" className="text-xs font-mono-brand uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300">
              ← Back to the site
            </Link>
          </div>
        )}

        {!error && !ctx && (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        )}

        {!error && ctx && ctx.already_reviewed && !done && (
          <div data-testid="review-already">
            <h1 className="text-3xl font-condensed font-extrabold uppercase mb-3">Already Received</h1>
            <p className="text-sm text-slate-400 mb-6">You've already told us about this job — thanks heaps, {ctx.name}.</p>
            <Link to="/" data-testid="review-home-link-2" className="text-xs font-mono-brand uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300">
              ← Back to the site
            </Link>
          </div>
        )}

        {!error && ctx && !ctx.already_reviewed && !done && (
          <form onSubmit={submit} data-testid="review-form">
            <p className="text-xs font-mono-brand uppercase tracking-[0.25em] text-amber-400 mb-2">Tell Us How We Did</p>
            <h1 className="text-3xl font-condensed font-extrabold uppercase mb-2">How's The Finish?</h1>
            <p className="text-sm text-slate-400 mb-6">{ctx.service_name} for {ctx.name} — {ctx.date}</p>
            <div className="flex gap-2 mb-6" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`review-star-${n}`} aria-label={`${n} stars`}
                        className="transition-transform duration-200 hover:scale-110">
                  <Star className={`w-9 h-9 ${n <= rating ? "text-amber-500 fill-amber-500" : "text-slate-700"}`} />
                </button>
              ))}
            </div>
            <label className="block text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-400 mb-2">A Few Words</label>
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="review-message-input"
                      placeholder="What did we fix, and how's it looking?"
                      className="w-full bg-[#0B0F17] border border-slate-700 focus:border-amber-500 outline-none px-4 py-3 text-slate-100 placeholder:text-slate-600 transition-colors mb-6" />
            <button type="submit" disabled={busy || !message.trim()} data-testid="review-submit-button"
                    className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-950 font-condensed font-bold uppercase tracking-wide py-3.5 transition-colors flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? "Sending…" : "Send Review"}
            </button>
          </form>
        )}

        {done && (
          <div data-testid="review-success" className="text-center py-4">
            <CheckCircle2 className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-3xl font-condensed font-extrabold uppercase mb-3">You're A Legend</h1>
            <p className="text-sm text-slate-400 mb-6">
              Thanks for the words — once the owner gives it a tick, it may appear on the site to help your neighbours find us.
            </p>
            <Link to="/" data-testid="review-home-link-3" className="text-xs font-mono-brand uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300">
              ← Back to the site
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
